import { input } from '@aws-amplify/ui/lib/Input.css'
import { DirectiveFunction } from 'vue'
import { DirectiveBinding } from 'vue/types/options'
import { VNode } from 'vue/types/umd'
const isTextAreaComposing = (el: HTMLInputElement) => {
  const v = el.value
  return el.selectionStart !== v.length || v.substr(v.length - 2, 1) !== '\n'
}
const isTextArea = (el: HTMLInputElement) => {
  return el.tagName.toUpperCase() === 'TEXTAREA'
}
const isAutoComplete = (el: HTMLInputElement) => {
  return !!el.closest('.v-autocomplete')
}

const isAutoCompleteMenuOpen = (el: HTMLInputElement) => {
  return el
    .closest('.v-autocomplete')
    ?.classList.contains('v-select--is-menu-active') ?? false
}

// If the input is a v-autocomplete using Japanese IME,
// an additional Enter event is required to select the highlighted item before moving to the next field.
const dispatchEnterEvent = (el: HTMLElement) => {
  const enterEvent = new KeyboardEvent('keydown', {
    key: 'Enter',
    code: 'Enter',
    keyCode: 13,
    which: 13,
    bubbles: true,
    cancelable: true
  })
  el.dispatchEvent(enterEvent)
}

const nextFocus: DirectiveFunction = (
  el: HTMLElement,
  binding: DirectiveBinding,
  vnode: VNode
) => {
  // Handle stop(stop to next to other input) option
  const option = binding?.arg
  const value = binding?.value

  let group: any
  let isStop = false
  let isDisabled = false
  switch (option) {
    case 'stop':
      group = 'default'
      isStop = true
      break
    default:
      group = option || 'default'
      break
  }
  if (value && value === 'disabled') isDisabled = true
  if (isDisabled) return
  bindInputGroup(el, group)

  // On Mac, compositionend fires before keyup, so ev.isComposing is already false
  // when the Enter keyup arrives. Track it manually to suppress focus move.
  let justFinishedComposing = false
  el.addEventListener('compositionend', () => {
    justFinishedComposing = true
  })

  // ダイアログのEnter決定などで「keydownは別要素・keyupだけがこの要素」に
  // 漏れて届くことがある。対応するkeydownを受けていないEnter keyupでは
  // フォーカス移動しない。
  let enterKeydownReceived = false
  el.addEventListener('keydown', (ev: KeyboardEvent) => {
    if (ev.key === 'Enter' && ev.isTrusted) enterKeydownReceived = true
  })

  el.addEventListener('keyup', (ev: KeyboardEvent) => {
    if (ev.key === 'Enter' && (ev.isComposing || justFinishedComposing)) {
      justFinishedComposing = false
      return
    }
    justFinishedComposing = false
    if (ev.key === 'Enter') {
      const hadKeydown = enterKeydownReceived
      enterKeydownReceived = false
      if (!hadKeydown) return
      // Find inputs only within the currently open dialog
      const dialogs = document.querySelectorAll('.v-dialog.v-dialog--active')
      // Get last dialog
      const dialog =
        dialogs.length > 0 ? dialogs[dialogs.length - 1] : undefined
      const doc = dialog ? dialog : document
      const inputs = Array.from(
        doc.querySelectorAll(
          `input:not([type="hidden"], [readonly], [disabled], [type="checkbox"])[group="${group}"], textarea:not([type="hidden"], [readonly], [disabled], [type="checkbox"])`
        )
      ) as HTMLElement[]
      const targetElement = ev.target as HTMLElement
      const currentIndex = inputs.findIndex(
        (input) => input.id === targetElement.id
      )
      if (currentIndex < 0) return
      // Check if it's text area
      if (
        isTextArea(inputs[currentIndex] as HTMLInputElement) &&
        isTextAreaComposing(inputs[currentIndex] as HTMLInputElement)
      )
        return
      if (isStop) return
      const nextIndex = currentIndex + 1

      // Handle for v-autocomplete
      // Only dispatch extra Enter when menu is open (e.g. Japanese IME case).
      // If the menu is already closed (item selected on first Enter), skip to avoid reopening.
      if (
        isAutoComplete(inputs[currentIndex] as HTMLInputElement) &&
        isAutoCompleteMenuOpen(inputs[currentIndex] as HTMLInputElement)
      ) {
        dispatchEnterEvent(inputs[currentIndex])
      }

      inputs[currentIndex].blur()
      if (nextIndex < inputs.length) {
        inputs[nextIndex].click()
      } else {
        // If it's the last input
        inputs[0].click()
      }
    }
  })
}
function bindInputGroup(el: HTMLElement, group: string) {
  const inputs = Array.from(
    el.querySelectorAll(
      `input:not([type="hidden"], [readonly], [disabled], [type="checkbox"])`
    )
  ) as HTMLElement[]
  inputs.forEach((i) => i.setAttribute('group', group))
}
export default nextFocus
